import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './supabaseClient';
import './ResetPassword.css';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // Check if user has a valid recovery session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Session error:', error);
          setMessage({ type: 'error', text: t('resetPassword.errors.invalidLink') });
          setIsValidSession(false);
        } else if (session) {
          setIsValidSession(true);
        } else {
          // Check URL for recovery token (Supabase adds these as hash params)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const type = hashParams.get('type');

          if (accessToken && type === 'recovery') {
            // Set the session with the recovery token
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || ''
            });

            if (sessionError) {
              setMessage({ type: 'error', text: t('resetPassword.errors.invalidLink') });
              setIsValidSession(false);
            } else {
              setIsValidSession(true);
            }
          } else {
            setMessage({ type: 'error', text: t('resetPassword.errors.invalidReset') });
            setIsValidSession(false);
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setMessage({ type: 'error', text: t('resetPassword.errors.generic') });
        setIsValidSession(false);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (password.length < 8) {
      setMessage({ type: 'error', text: t('resetPassword.errors.minLength') });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: t('resetPassword.errors.noMatch') });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setMessage({ type: 'success', text: t('resetPassword.errors.success') });

      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: error.message || t('resetPassword.errors.failed') });
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="reset-password-page">
        <div className="reset-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('resetPassword.verifying')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-container">
        <a href="/" className="back-to-home">
          <span className="back-arrow">←</span>
          {t('resetPassword.back')}
        </a>

        <div className="reset-card">
          <div className="reset-header">
            <div className="logo-icon">
              <img
                src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/reveel-icon-v2.png"
                alt="Reveel"
              />
            </div>
            <h1 className="reset-title">{t('resetPassword.title')}</h1>
            <p className="reset-subtitle">
              {isValidSession
                ? t('resetPassword.subtitle')
                : t('resetPassword.problemLink')}
            </p>
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.type === 'success' && <span className="message-icon">✓</span>}
              {message.type === 'error' && <span className="message-icon">!</span>}
              {message.text}
            </div>
          )}

          {isValidSession ? (
            <form onSubmit={handleSubmit} className="reset-form">
              <div className="form-group">
                <label className="form-label">{t('resetPassword.newPassword')}</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  required
                  minLength={8}
                  disabled={loading}
                />
                <small className="form-hint">{t('resetPassword.minChars')}</small>
              </div>

              <div className="form-group">
                <label className="form-label">{t('resetPassword.confirmPassword')}</label>
                <input
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('resetPassword.confirmPlaceholder')}
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('resetPassword.updating')}
                  </>
                ) : (
                  t('resetPassword.updateButton')
                )}
              </button>
            </form>
          ) : (
            <div className="invalid-link-actions">
              <p className="help-text">
                {t('resetPassword.expiredText')}
              </p>
              <a href="/" className="btn-home">
                {t('resetPassword.returnHome')}
              </a>
            </div>
          )}
        </div>

        <div className="reset-footer">
          <p>{t('resetPassword.needHelp')} <a href="mailto:support@reveel.health">{t('resetPassword.contactSupport')}</a></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
